import type { BackgroundPatternName } from "./Background.types";

export type PatternTile = {
  width: number;
  height: number;
  paths: string[];
  dots: { cx: number; cy: number; r: number }[];
  dashed: boolean;
};

const HEX_ROW_RATIO = Math.sqrt(3);

// One pointy-top hexagon, side `s`, centred on (cx, cy).
function hexagon(cx: number, cy: number, s: number): string {
  const halfWidth = (HEX_ROW_RATIO * s) / 2;
  const half = s / 2;

  return [
    `M${cx} ${cy - s}`,
    `L${cx - halfWidth} ${cy - half}`,
    `L${cx - halfWidth} ${cy + half}`,
    `L${cx} ${cy + s}`,
    `L${cx + halfWidth} ${cy + half}`,
    `L${cx + halfWidth} ${cy - half}`,
    "Z",
  ].join(" ");
}

// The tile is what `<pattern>` repeats. Anything drawn past its edges is
// clipped, so the honeycomb also draws the hexagons straddling them: their
// clipped halves are what the neighbouring tiles are missing.
export function buildPatternTile(
  pattern: BackgroundPatternName,
  cellSize: number,
): PatternTile {
  if (pattern === "hexagons") {
    const side = cellSize;
    const width = HEX_ROW_RATIO * side;
    const height = 3 * side;

    return {
      width,
      height,
      paths: [
        hexagon(width / 2, side, side),
        hexagon(0, 2.5 * side, side),
        hexagon(width, 2.5 * side, side),
        hexagon(0, -0.5 * side, side),
        hexagon(width, -0.5 * side, side),
      ],
      dots: [],
      dashed: false,
    };
  }

  if (pattern === "dots") {
    return {
      width: cellSize,
      height: cellSize,
      paths: [],
      dots: [{ cx: cellSize / 2, cy: cellSize / 2, r: 1 }],
      dashed: false,
    };
  }

  // Two edges of the cell, so the repeat draws a full grid
  return {
    width: cellSize,
    height: cellSize,
    paths: [`M${cellSize} 0 L0 0 L0 ${cellSize}`],
    dots: [],
    dashed: pattern === "dashed-grid",
  };
}
